/**
 * Support Tickets API Service
 * 
 * Centralized API client for support ticket operations.
 * Uses Axios with JWT token injection via interceptors (consistent with app pattern)
 */

import axios from 'axios';
import { authService } from './authService';
import { errorLogService } from './errorLogService';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:5001/api';

// Create Axios instance with proper interceptors
const api = axios.create({
  baseURL: `${API_BASE_URL}/support`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add JWT token via interceptor (same pattern as other services)
api.interceptors.request.use(
  (config) => {
    const token = authService.getToken();
    
    if (token && authService.isTokenValid()) {
      config.headers.Authorization = `Bearer ${token}`;
    } else if (token) {
      // Token expired - redirect to login
      console.warn('[SUPPORT-API] Token expirado, redirecionando para login...');
      const currentPath = window.location.pathname + window.location.search;
      authService.removeToken();
      authService.redirectToLogin(currentPath);
      return Promise.reject(new Error('Token expirado'));
    }
    
    return config;
  },
  (error) => Promise.reject(error)
);

// Handle response errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const message = error.response?.data?.message || error.message;
    console.error('[SUPPORT-API] Error:', message);
    
    // Log error for debugging
    if (errorLogService) {
      errorLogService.logError({
        source: 'supportService',
        message,
        error: error.response?.data || error,
      }).catch(() => {});
    }
    
    return Promise.reject(error);
  }
);

/**
 * List support tickets with filters and pagination
 * 
 * @param {Object} params - Query parameters
 * @param {string} [params.searchText] - Search by subject or user name
 * @param {number} [params.status] - Filter by status (0=Open, 1=InProgress, 2=Resolved, 3=Closed)
 * @param {number} [params.priority] - Filter by priority (0=Low, 1=Normal, 2=High, 3=Urgent)
 * @param {number} [params.page] - Page number (1-based), default 1
 * @param {number} [params.pageSize] - Items per page, default 20
 * 
 * @returns {Promise<Object>} { tickets, totalCount, page, pageSize, totalPages }
 * @throws {Error} API error
 */
export async function getTickets({
  searchText = '',
  status = null,
  priority = null,
  page = 1,
  pageSize = 20,
} = {}) {
  const skip = (page - 1) * pageSize;
  
  try {
    const response = await api.get('/', {
      params: {
        skip,
        take: pageSize,
        ...(searchText?.trim() && { search: searchText.trim() }),
        ...(status !== null && status !== '' && { status }),
        ...(priority !== null && priority !== '' && { priority }),
      },
    });

    return {
      tickets: response.data.tickets || [],
      totalCount: response.data.totalCount || 0,
      page,
      pageSize,
      totalPages: Math.ceil((response.data.totalCount || 0) / pageSize),
    };
  } catch (error) {
    throw error;
  }
}

/**
 * Get ticket details with replies
 * 
 * @param {string} ticketId - Ticket UUID
 * @returns {Promise<Object>} { ticket, replies }
 * @throws {Error} API error
 */
export async function getTicketDetails(ticketId) {
  if (!ticketId?.trim()) {
    throw new Error('Ticket ID is required');
  }

  try {
    const response = await api.get(`/${ticketId}`);
    
    return {
      ticket: response.data.ticket || {},
      replies: response.data.replies || [],
    };
  } catch (error) {
    throw error;
  }
}

/**
 * Create reply on a ticket
 * 
 * @param {string} ticketId - Ticket UUID
 * @param {string} content - HTML content of the reply
 * @returns {Promise<Object>} New reply object
 * @throws {Error} API error
 */
export async function createReply(ticketId, content) {
  if (!ticketId?.trim()) {
    throw new Error('Ticket ID is required');
  }
  if (!content?.trim()) {
    throw new Error('Reply content cannot be empty');
  }

  try {
    const response = await api.post(`/${ticketId}/replies`, {
      content: content.trim(),
    });
    
    return response.data;
  } catch (error) {
    throw error;
  }
}

/**
 * Update ticket status
 * 
 * @param {string} ticketId - Ticket UUID
 * @param {number} status - New status (0=Open, 1=InProgress, 2=Resolved, 3=Closed)
 * @returns {Promise<Object>} Updated ticket object
 * @throws {Error} API error
 */
export async function updateTicketStatus(ticketId, status) {
  if (!ticketId?.trim()) {
    throw new Error('Ticket ID is required');
  }
  if (typeof status !== 'number' || status < 0 || status > 3) {
    throw new Error('Invalid status value');
  }

  try {
    const response = await api.put(`/${ticketId}/status`, { status });
    return response.data;
  } catch (error) {
    throw error;
  }
}

/**
 * Get support tickets summary statistics
 * Admin dashboard overview (cached by backend)
 * 
 * @returns {Promise<Object>} { openCount, inProgressCount, resolvedCount, closedCount, averageResponseTime }
 * @throws {Error} API error
 */
export async function getTicketsStats() {
  try {
    const response = await api.get('/summary');
    return response.data;
  } catch (error) {
    throw error;
  }
}

export default {
  getTickets,
  getTicketDetails,
  createReply,
  updateTicketStatus,
  getTicketsStats,
};
